import React, { useState, useEffect, useRef } from 'react';
import BrandingPage from './service/Branding';
import DigitalMarketing from './service/Digital-Marketing';
import VideoProduction from './service/Video-Production';
import Design from './service/Design';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullhorn, faPaintBrush, faBullseye, faVideo } from '@fortawesome/free-solid-svg-icons';

const Service = () => {
    const services = [
        { id: 'branding', label: 'Branding', icon: faBullhorn },
        { id: 'digital-marketing', label: 'Digital Marketing', icon: faBullseye },
        { id: 'video-production', label: 'Video Production', icon: faVideo }
    ];
    
    const [selectedService, setSelectedService] = useState(services[0].id);
    const [userInteracted, setUserInteracted] = useState(false);
    const intervalRef = useRef(null);
    const idleTimeoutRef = useRef(null);
    const idleTime = 10000; // 10 seconds of inactivity before resuming rotation

    const startAutoRotation = () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        
        intervalRef.current = setInterval(() => {
            const currentIndex = services.findIndex(s => s.id === selectedService);
            const nextIndex = (currentIndex + 1) % services.length;
            setSelectedService(services[nextIndex].id);
        }, 5000); // Rotate every 5 seconds
    };

    const handleUserActivity = () => {
        // Clear any pending idle timeout
        if (idleTimeoutRef.current) {
            clearTimeout(idleTimeoutRef.current);
        }
        
        // If user was previously idle, mark as active
        if (userInteracted) {
            setUserInteracted(false);
        }
        
        // Set new timeout to detect when user becomes idle
        idleTimeoutRef.current = setTimeout(() => {
            setUserInteracted(true);
            startAutoRotation();
        }, idleTime);
    };

    // Setup and cleanup
    useEffect(() => {
        // Initial auto-rotation
        startAutoRotation();
        
        // Set up activity detectors
        window.addEventListener('mousemove', handleUserActivity);
        window.addEventListener('keydown', handleUserActivity);
        window.addEventListener('scroll', handleUserActivity);
        window.addEventListener('click', handleUserActivity);
        window.addEventListener('touchstart', handleUserActivity);

        return () => {
            // Cleanup
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
            
            window.removeEventListener('mousemove', handleUserActivity);
            window.removeEventListener('keydown', handleUserActivity);
            window.removeEventListener('scroll', handleUserActivity);
            window.removeEventListener('click', handleUserActivity);
            window.removeEventListener('touchstart', handleUserActivity);
        };
    }, []);

    // Handle service selection
    const handleServiceSelect = (serviceId) => {
        // Mark user interaction
        setUserInteracted(true);
        
        // Clear current rotation
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        
        // Set new service
        setSelectedService(serviceId);
        
        // Restart idle detection
        handleUserActivity();
    };

    const renderServiceContent = () => {
        switch (selectedService) {
            case 'branding':
                return <BrandingPage />;
            case 'design':
                return <Design />;
            case 'digital-marketing':
                return <DigitalMarketing />;
            case 'video-production':
                return <VideoProduction />;
            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen w-full mt-20">
            {/* Navigation Bar */}
            <nav className="flex flex-col sm:flex-row lg:flex-nowrap justify-center items-center w-full text-[#FD5F20]">
                <div className="flex flex-col sm:flex-row w-full">
                    {services.map((service) => (
                        <button
                            key={service.id}
                            onClick={() => handleServiceSelect(service.id)}
                            className={`flex items-center justify-center gap-2 px-6 py-3 text-base font-semibold transition-colors w-full sm:w-auto border rounded-lg mx-1 ${
                                selectedService === service.id
                                    ? 'bg-[#FD5F20] text-white border-[#FD5F20]'
                                    : 'bg-[#FD5F20]/60 text-white border-transparent hover:bg-[#FD5F20]/80'
                            }`}
                        >
                            <FontAwesomeIcon icon={service.icon} />
                            {service.label}
                        </button>
                    ))}
                </div>
            </nav>

            {/* Service Content */}
            <div className="pt-8 overflow-hidden">
                {renderServiceContent()}
            </div>
        </div>
    );
};

export default Service;